import type React from "react";

import { colorsPalettes } from "@similarweb/styles";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import {
    StyledColorCircle,
    StyledEditInput,
    StyledEditSection,
    StyledIconButton,
    StyledSaveButton,
} from "app/pages/sales-intelligence/modules/lists/components/cells/TagCell/styles";

const TAG_COLOR_OPTIONS = [
    colorsPalettes.carbon[250],
    colorsPalettes.sky[400],
    colorsPalettes.green[300],
    colorsPalettes.carbon[400],
    colorsPalettes.red[500],
    colorsPalettes.purple[300],
    colorsPalettes.orange[400],
    colorsPalettes.green[600],
];

type TagEditFormProps = {
    mode: "edit" | "create";
    selectedColor: string;
    defaultInputValue?: string;
    isSaveDisabled: boolean;
    onColorChange: (color: string) => void;
    onInputChange: (value: string) => void;
    onSave: (event: React.SyntheticEvent) => void;
    onCancel: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

const CONFIG = {
    edit: {
        colorLabelKey: "si.lists.tag.edit.color",
        saveLabelKey: "global.save",
        automationPrefix: "lists-tag-edit",
    },
    create: {
        colorLabelKey: "si.lists.tag.add.color",
        saveLabelKey: "common.add",
        automationPrefix: "lists-tag-add",
    },
} as const;

export const TagEditForm: React.FC<TagEditFormProps> = ({
    mode,
    selectedColor,
    defaultInputValue,
    isSaveDisabled,
    onColorChange,
    onInputChange,
    onSave,
    onCancel,
}) => {
    const { colorLabelKey, saveLabelKey, automationPrefix } = CONFIG[mode];

    return (
        <StyledEditSection
            gap="8px"
            paddingX="12px"
            paddingY="8px"
            onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
            }}
            data-automation={`${automationPrefix}-section`}
        >
            <Text
                fontVariant="caption"
                color={colorsPalettes.carbon[300]}
            >
                {translate(colorLabelKey)}
            </Text>
            <Flex
                gap="4px"
                flexWrap="wrap"
            >
                {TAG_COLOR_OPTIONS.map((color) => (
                    <StyledColorCircle
                        key={color}
                        $color={color}
                        $isSelected={selectedColor === color}
                        onClick={() => onColorChange(color)}
                        data-automation={`${automationPrefix}-color-${color}`}
                    />
                ))}
            </Flex>
            <Flex
                alignItems="center"
                gap="4px"
            >
                <StyledEditInput
                    defaultValue={defaultInputValue}
                    onChange={onInputChange}
                    autoFocus
                    dataAutomation={`${automationPrefix}-input`}
                />
                <StyledSaveButton
                    type="flat"
                    onClick={onSave}
                    isDisabled={isSaveDisabled}
                    data-automation={`${automationPrefix}-save`}
                    color={colorsPalettes.carbon[200]}
                >
                    {translate(saveLabelKey)}
                </StyledSaveButton>
                <StyledIconButton
                    type="flat"
                    iconName="close"
                    iconSize="10px"
                    onClick={onCancel}
                    data-automation={`${automationPrefix}-cancel`}
                    color={colorsPalettes.carbon[200]}
                />
            </Flex>
        </StyledEditSection>
    );
};
